import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  CheckCircle2,
  ShieldCheck,
  Clock,
  Phone,
  Mail,
  ArrowLeft,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, BUSINESS } from "@/lib/api";
import { QUOTE_CONFIG } from "@/lib/quoteConfig";

const TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
  "4:00 PM", "5:00 PM", "6:00 PM",
];

function QuestionField({ q, value, onChange }) {
  const common = {
    id: q.id,
    "data-testid": `quote-q-${q.id}`,
    required: q.required,
    placeholder: q.placeholder,
    value: value ?? "",
    onChange: (e) => onChange(q.id, e.target.value),
  };

  if (q.type === "textarea") {
    return <Textarea {...common} rows={4} className="mt-2" />;
  }
  if (q.type === "select") {
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(q.id, v)}>
        <SelectTrigger className="mt-2 h-11" data-testid={`quote-q-${q.id}`}>
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {q.options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (q.type === "number") {
    return (
      <Input
        type="number"
        min={q.min}
        max={q.max}
        {...common}
        className="mt-2 h-11"
      />
    );
  }
  return <Input {...common} className="mt-2 h-11" />;
}

export default function QuotePage() {
  const { slug } = useParams();
  const config = QUOTE_CONFIG[slug];

  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    date: undefined, time: "", address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!config) return <Navigate to="/booking" replace />;

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const updateAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required custom questions
    for (const q of config.questions) {
      if (q.required && !answers[q.id]) {
        toast.error(`Please answer: ${q.label}`);
        return;
      }
    }
    if (!form.name || !form.phone || !form.email || !form.date || !form.time || !form.address) {
      toast.error("Please fill all required contact fields.");
      return;
    }

    // Determine service_type
    let service_type = config.serviceType;
    if (!service_type) {
      service_type = answers.primary;
    }
    if (!service_type) {
      toast.error("Please choose a primary service.");
      return;
    }

    // Build description from answers + notes
    const lines = [
      `[${config.title}]`,
      ...config.questions
        .filter((q) => answers[q.id])
        .map((q) => `${q.label}: ${answers[q.id]}`),
    ];
    if (form.notes) {
      lines.push("", `Additional notes: ${form.notes}`);
    }
    const description = lines.join("\n");
    const preferred_datetime = `${format(form.date, "yyyy-MM-dd")} ${form.time}`;

    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service_type,
        preferred_datetime,
        address: form.address,
        description,
      });
      setSuccess(true);
      toast.success("Quote request received! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <Link
          to="/"
          data-testid="quote-back"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — context */}
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
              {config.kind === "service" ? "Tailored Quote" : "For " + config.title.replace(" Quote", "")}
            </p>
            <h1
              data-testid="quote-headline"
              className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
            >
              {config.headline}
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">{config.intro}</p>

            {config.perfectFor && (
              <div className="mt-8">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">
                  Perfect for
                </p>
                <ul className="space-y-2">
                  {config.perfectFor.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 space-y-4">
              {[
                { icon: ShieldCheck, label: "Insured & vetted technicians" },
                { icon: Clock, label: "Same-day appointments available" },
                { icon: CheckCircle2, label: "Transparent, upfront pricing" },
              ].map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.label} className="flex items-start gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <p className="text-sm text-slate-700 mt-1">{t.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 p-6 rounded-2xl bg-slate-900 text-white">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400">
                Prefer to call?
              </p>
              <p className="mt-2 text-lg flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <a href={BUSINESS.phoneHref}>{BUSINESS.phone}</a>
              </p>
              <p className="mt-1 text-sm text-slate-300 flex items-center gap-2">
                <Mail className="h-4 w-4" /> {BUSINESS.email}
              </p>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm">
              {success ? (
                <div data-testid="quote-success" className="text-center py-10">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                  <h2 className="pf-heading mt-5 text-2xl font-semibold text-slate-900">
                    Quote request received!
                  </h2>
                  <p className="mt-3 text-slate-600">
                    We’ll be in touch shortly with a tailored quote and next steps.
                  </p>
                  <div className="mt-8 flex justify-center gap-3">
                    <Link
                      to="/"
                      data-testid="quote-success-home"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Back to home
                    </Link>
                    <Link
                      to="/specials"
                      data-testid="quote-success-specials"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 border border-slate-200 text-sm font-semibold hover:bg-slate-50 transition-colors"
                    >
                      View Specials
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                  <div>
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      Tell us about the job
                    </h2>
                    <div className="mt-4 space-y-5">
                      {config.questions.map((q) => (
                        <div key={q.id}>
                          <Label htmlFor={q.id} className="text-sm font-medium text-slate-700">
                            {q.label}{q.required && " *"}
                          </Label>
                          <QuestionField
                            q={q}
                            value={answers[q.id]}
                            onChange={updateAnswer}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      Your contact info
                    </h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name">Full name *</Label>
                        <Input
                          id="name"
                          data-testid="quote-input-name"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          placeholder="Jane Doe"
                          className="mt-2 h-11"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          data-testid="quote-input-phone"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          placeholder="(404) 555-0123"
                          className="mt-2 h-11"
                          required
                        />
                      </div>
                    </div>
                    <div className="mt-5">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="quote-input-email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@example.com"
                        className="mt-2 h-11"
                        required
                      />
                    </div>
                    <div className="mt-5">
                      <Label htmlFor="address">Service address *</Label>
                      <Input
                        id="address"
                        data-testid="quote-input-address"
                        value={form.address}
                        onChange={(e) => update("address", e.target.value)}
                        placeholder="123 Peachtree St, Atlanta, GA"
                        className="mt-2 h-11"
                        required
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      Preferred date &amp; time
                    </h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Date *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              data-testid="quote-date-trigger"
                              className="mt-2 w-full h-11 flex items-center justify-between px-3 rounded-md border border-input bg-white text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <span>{form.date ? format(form.date, "PPP") : "Pick a date"}</span>
                              <CalendarIcon className="h-4 w-4 text-slate-400" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={form.date}
                              onSelect={(d) => update("date", d)}
                              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Time *</Label>
                        <Select value={form.time} onValueChange={(v) => update("time", v)}>
                          <SelectTrigger data-testid="quote-select-time" className="mt-2 h-11">
                            <SelectValue placeholder="Pick a time" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_SLOTS.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-5">
                      <Label htmlFor="notes">Anything else we should know?</Label>
                      <Textarea
                        id="notes"
                        data-testid="quote-input-notes"
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        placeholder="Optional context, access notes, parking, etc."
                        rows={3}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="quote-submit"
                    className="w-full h-12 rounded-xl bg-slate-900 hover:bg-blue-700 text-white text-base font-semibold"
                  >
                    {submitting ? "Submitting…" : "Submit Quote Request"}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">
                    We’ll only use your info to complete this quote request.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
